import { getApiError } from '../lib/apiError';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Package, Loader2, AlertTriangle, CheckSquare, X, Printer, ScanBarcode } from 'lucide-react';
import JsBarcode from 'jsbarcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [eanSearch, setEanSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '', sku: '', description: '', category: '', price: '', cost_price: '',
    stock: '', min_stock: '', unit: 'un', supplier_id: '',
    ncm: '', cest: '', origem: '0', cfop: '5102', csosn: '102', 
    cst_pis: '99', cst_cofins: '99', ean: ''
  });

  // Bulk edit state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkData, setBulkData] = useState({ price: '', category: '', stock: '', cfop: '' });
  const [bulkFields, setBulkFields] = useState({ price: false, category: false, stock: false, cfop: false });

  // Label print state
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelQuantities, setLabelQuantities] = useState({});

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, per_page: 100, total_count: 0, total_pages: 1 });

  useEffect(() => {
    // Carrega dados auxiliares (fornecedores e categorias) apenas uma vez
    const loadAuxData = async () => {
      try {
        const [suppliersRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/api/suppliers`),
          axios.get(`${API_URL}/api/products/categories/list`)
        ]);
        setSuppliers(suppliersRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        toast.error('Erro ao carregar dados auxiliares');
      }
    };
    loadAuxData();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: 100 };
      if (eanSearch) {
        params.search = eanSearch;
      } else if (search) {
        params.search = search;
      }
      if (filterCategory) params.category = filterCategory;
      if (filterLowStock) params.low_stock = true;
      const response = await axios.get(`${API_URL}/api/products`, { params });
      const pData = response.data;
      setProducts(pData.products || pData);
      setPagination(pData.pagination || { page: 1, per_page: 100, total_count: 0, total_pages: 1 });
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, [search, eanSearch, filterCategory, filterLowStock, currentPage]);

  const resetForm = () => {
    setFormData({
      name: '', sku: '', description: '', category: '', price: '', cost_price: '',
      stock: '', min_stock: '', unit: 'un', supplier_id: '',
      ncm: '', cest: '', origem: '0', cfop: '5102', csosn: '102', 
      cst_pis: '99', cst_cofins: '99', ean: ''
    });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '', sku: product.sku || '', description: product.description || '',
      category: product.category || '', price: product.price?.toString() || '',
      cost_price: product.cost_price?.toString() || '', stock: product.stock?.toString() || '',
      min_stock: product.min_stock?.toString() || '', unit: product.unit || 'un',
      supplier_id: product.supplier_id || '',
      ncm: product.ncm || '', cest: product.cest || '',
      origem: product.origem?.toString() || '0', cfop: product.cfop || '5102',
      csosn: product.csosn || '102', cst_pis: product.cst_pis || '99',
      cst_cofins: product.cst_cofins || '99', ean: product.ean || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Nome e preço são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        cost_price: parseFloat(formData.cost_price) || 0,
        stock: parseInt(formData.stock) || 0,
        min_stock: parseInt(formData.min_stock) || 0,
        origem: parseInt(formData.origem) || 0,
        ncm: formData.ncm || '00000000', cfop: formData.cfop || '5102',
        csosn: formData.csosn || '102', cst_pis: formData.cst_pis || '99',
        cst_cofins: formData.cst_cofins || '99', ean: formData.ean || 'SEM GTIN',
        cest: formData.cest || null
      };
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, data);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/api/products`, data);
        toast.success('Produto cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao salvar produto'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}`);
      toast.success('Produto excluído com sucesso!');
      setSelectedIds(prev => prev.filter(sid => sid !== id));
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  // Bulk selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === products.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(products.map(p => p.id));
    }
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir ${selectedIds.length} produto(s)? Esta ação não pode ser desfeita.`)) return;
    try {
      const response = await axios.delete(`${API_URL}/api/products/bulk-delete`, {
        data: { product_ids: selectedIds }
      });
      toast.success(response.data.message);
      setSelectedIds([]);
      fetchProducts();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao excluir produtos'));
    }
  };

  const openBulkEdit = () => {
    setBulkData({ price: '', category: '', stock: '', cfop: '' });
    setBulkFields({ price: false, category: false, stock: false, cfop: false });
    setBulkDialogOpen(true);
  };

  const handleBulkSubmit = async () => {
    const updates = {};
    if (bulkFields.price && bulkData.price !== '') updates.price = parseFloat(bulkData.price);
    if (bulkFields.category && bulkData.category !== '') updates.category = bulkData.category;
    if (bulkFields.stock && bulkData.stock !== '') updates.stock = parseInt(bulkData.stock);
    if (bulkFields.cfop && bulkData.cfop !== '') updates.cfop = bulkData.cfop;

    if (Object.keys(updates).length === 0) {
      toast.error('Marque pelo menos um campo e preencha o valor');
      return;
    }

    setBulkSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/products/bulk-update`, {
        product_ids: selectedIds,
        updates
      });
      toast.success(response.data.message);
      setBulkDialogOpen(false);
      setSelectedIds([]);
      fetchProducts();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao atualizar produtos'));
    } finally {
      setBulkSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const isLowStock = (product) => product.stock <= product.min_stock;

  // === Label Printing ===
  const openLabelDialog = () => {
    const defaultQty = {};
    selectedIds.forEach(id => { defaultQty[id] = 1; });
    setLabelQuantities(defaultQty);
    setLabelDialogOpen(true);
  };

  const printLabels = () => {
    const selectedProducts = products.filter(p => selectedIds.includes(p.id));
    if (selectedProducts.length === 0) return;

    // Build labels array with quantities
    const labels = [];
    selectedProducts.forEach(product => {
      const qty = parseInt(labelQuantities[product.id]) || 1;
      for (let i = 0; i < qty; i++) {
        labels.push(product);
      }
    });

    // Open print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Popup bloqueado. Permita popups para imprimir etiquetas.');
      return;
    }

    const labelsHtml = labels.map((product, idx) => {
      const barcodeValue = (product.ean && product.ean !== 'SEM GTIN' && product.ean.length >= 4)
        ? product.ean
        : (product.sku || product.id.substring(0, 12));
      const displayName = product.name.length > 40 ? product.name.substring(0, 40) + '...' : product.name;
      const price = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(product.price);

      return `
        <div class="label">
          <div class="product-name">${displayName}</div>
          <div class="price">${price}</div>
          <svg class="barcode" id="barcode-${idx}"></svg>
          <div class="barcode-text">${barcodeValue}</div>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiquetas de Precos</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, Helvetica, sans-serif; }

          @page {
            size: auto;
            margin: 5mm;
          }

          .labels-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 2mm;
            justify-content: flex-start;
          }

          .label {
            width: 50mm;
            height: 30mm;
            border: 0.3mm dashed #ccc;
            padding: 1.5mm 2mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            overflow: hidden;
            page-break-inside: avoid;
          }

          .product-name {
            font-size: 7pt;
            font-weight: 700;
            text-align: center;
            line-height: 1.15;
            max-height: 8mm;
            overflow: hidden;
            width: 100%;
            text-transform: uppercase;
          }

          .price {
            font-size: 13pt;
            font-weight: 900;
            text-align: center;
            color: #000;
            letter-spacing: -0.3px;
          }

          .barcode {
            width: 42mm;
            height: 10mm;
          }

          .barcode-text {
            font-size: 5.5pt;
            color: #333;
            text-align: center;
          }

          @media print {
            body { -webkit-print-color-adjust: exact; }
            .label { border: 0.2mm dashed #ddd; }
            .no-print { display: none !important; }
          }

          .toolbar {
            padding: 10px 20px;
            background: #f0f4f8;
            border-bottom: 1px solid #ddd;
            display: flex;
            align-items: center;
            gap: 12px;
            font-family: Arial, sans-serif;
          }
          .toolbar button {
            padding: 8px 20px;
            background: #2563eb;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
          }
          .toolbar button:hover { background: #1d4ed8; }
          .toolbar span { font-size: 13px; color: #555; }
        </style>
      </head>
      <body>
        <div class="toolbar no-print">
          <button onclick="window.print()">Imprimir</button>
          <span>${labels.length} etiqueta(s) de ${selectedProducts.length} produto(s)</span>
        </div>
        <div class="labels-grid" style="padding: 3mm;">
          ${labelsHtml}
        </div>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <script>
          document.addEventListener('DOMContentLoaded', function() {
            ${labels.map((product, idx) => {
              const barcodeValue = (product.ean && product.ean !== 'SEM GTIN' && product.ean.length >= 4)
                ? product.ean
                : (product.sku || product.id.substring(0, 12));
              return `
                try {
                  JsBarcode("#barcode-${idx}", "${barcodeValue}", {
                    format: "${(product.ean && product.ean !== 'SEM GTIN' && /^\\d{8,13}$/.test(product.ean)) ? 'EAN13' : 'CODE128'}",
                    width: 1.2,
                    height: 28,
                    displayValue: false,
                    margin: 0
                  });
                } catch(e) {
                  JsBarcode("#barcode-${idx}", "${barcodeValue}", {
                    format: "CODE128",
                    width: 1.2,
                    height: 28,
                    displayValue: false,
                    margin: 0
                  });
                }
              `;
            }).join('\n')}
          });
        <\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setLabelDialogOpen(false);
    toast.success(`${labels.length} etiqueta(s) gerada(s) para impressao`);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Produtos</h1>
          <p className="text-slate-500 mt-1">Gerencie seu catálogo e estoque</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button onClick={openBulkEdit} className="bg-amber-500 hover:bg-amber-600" data-testid="bulk-edit-btn">
              <CheckSquare size={18} className="mr-2" />
              Editar {selectedIds.length} selecionado(s)
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-product-btn">
                <Plus size={18} className="mr-2" />
                Novo Produto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Nome do produto" data-testid="product-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sku">SKU</Label>
                    <Input id="sku" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} placeholder="Código do produto" data-testid="product-sku" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Descrição do produto" data-testid="product-description" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Categoria</Label>
                    <Input id="category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="Ex: Calçados" data-testid="product-category" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier">Fornecedor</Label>
                    <Select value={formData.supplier_id || "none"} onValueChange={(value) => setFormData({ ...formData, supplier_id: value === "none" ? "" : value })}>
                      <SelectTrigger data-testid="product-supplier"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price">Preço de Venda *</Label>
                    <Input id="price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="0,00" data-testid="product-price" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cost_price">Preço de Custo</Label>
                    <Input id="cost_price" type="number" step="0.01" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} placeholder="0,00" data-testid="product-cost" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stock">Estoque Atual</Label>
                    <Input id="stock" type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} placeholder="0" data-testid="product-stock" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="min_stock">Estoque Mínimo</Label>
                    <Input id="min_stock" type="number" value={formData.min_stock} onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })} placeholder="0" data-testid="product-minstock" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit">Unidade</Label>
                    <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                      <SelectTrigger data-testid="product-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="un">Unidade (un)</SelectItem>
                        <SelectItem value="kg">Quilograma (kg)</SelectItem>
                        <SelectItem value="m">Metro (m)</SelectItem>
                        <SelectItem value="l">Litro (l)</SelectItem>
                        <SelectItem value="cx">Caixa (cx)</SelectItem>
                        <SelectItem value="PAR">Par (PAR)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Dados Fiscais */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Dados Fiscais (NFC-e)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ncm">NCM (8 dígitos)</Label>
                      <Input id="ncm" value={formData.ncm} onChange={(e) => setFormData({ ...formData, ncm: e.target.value.replace(/\D/g, '').slice(0, 8) })} placeholder="00000000" maxLength={8} data-testid="product-ncm" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cest">CEST (opcional)</Label>
                      <Input id="cest" value={formData.cest} onChange={(e) => setFormData({ ...formData, cest: e.target.value.replace(/\D/g, '').slice(0, 7) })} placeholder="0000000" maxLength={7} data-testid="product-cest" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ean">EAN/GTIN</Label>
                      <Input id="ean" value={formData.ean} onChange={(e) => setFormData({ ...formData, ean: e.target.value })} placeholder="SEM GTIN" data-testid="product-ean" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="origem">Origem</Label>
                      <Select value={formData.origem} onValueChange={(value) => setFormData({ ...formData, origem: value })}>
                        <SelectTrigger data-testid="product-origem"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0 - Nacional</SelectItem>
                          <SelectItem value="1">1 - Estrangeira (importação direta)</SelectItem>
                          <SelectItem value="2">2 - Estrangeira (mercado interno)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cfop">CFOP</Label>
                      <Select value={formData.cfop} onValueChange={(value) => setFormData({ ...formData, cfop: value })}>
                        <SelectTrigger data-testid="product-cfop"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5102">5102 - Venda mercadoria adquirida</SelectItem>
                          <SelectItem value="5101">5101 - Venda produção</SelectItem>
                          <SelectItem value="5405">5405 - Venda com ST</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="csosn">CSOSN</Label>
                      <Select value={formData.csosn} onValueChange={(value) => setFormData({ ...formData, csosn: value })}>
                        <SelectTrigger data-testid="product-csosn"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="102">102 - Tributada sem crédito</SelectItem>
                          <SelectItem value="103">103 - Isenção ICMS</SelectItem>
                          <SelectItem value="500">500 - ICMS cobrado por ST</SelectItem>
                          <SelectItem value="900">900 - Outros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-product-btn">
                    {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                    {editingProduct ? 'Atualizar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Selection Bar */}
      {selectedIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="selection-bar">
          <span className="text-sm font-medium text-blue-800">
            {selectedIds.length} produto(s) selecionado(s)
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={clearSelection} className="text-slate-600">
              <X size={14} className="mr-1" /> Limpar
            </Button>
            <Button size="sm" onClick={openBulkEdit} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="bulk-edit-bar-btn">
              <Pencil size={14} className="mr-1" /> Editar em Massa
            </Button>
            <Button size="sm" onClick={openLabelDialog} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-label-bar-btn">
              <Printer size={14} className="mr-1" /> Imprimir Etiquetas
            </Button>
            <Button size="sm" onClick={handleBulkDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="bulk-delete-bar-btn">
              <Trash2 size={14} className="mr-1" /> Excluir Selecionados
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input placeholder="Buscar por nome ou SKU..." value={search} onChange={(e) => { setSearch(e.target.value); setEanSearch(''); setCurrentPage(1); }} className="pl-10" data-testid="search-products" />
            </div>
            <div className="relative w-full sm:w-56">
              <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Código de barras..."
                value={eanSearch}
                onChange={(e) => { setEanSearch(e.target.value); if (e.target.value) setSearch(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setCurrentPage(1);
                    fetchProducts();
                  }
                }}
                className="pl-10"
                data-testid="search-ean"
              />
            </div>
            <Select value={filterCategory || "all"} onValueChange={(v) => { setFilterCategory(v === "all" ? "" : v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-48"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant={filterLowStock ? 'default' : 'outline'} onClick={() => { setFilterLowStock(!filterLowStock); setCurrentPage(1); }} className={filterLowStock ? 'bg-amber-500 hover:bg-amber-600' : ''}>
              <AlertTriangle size={18} className="mr-2" /> Estoque Baixo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Package size={20} className="text-blue-600" />
            Lista de Produtos ({pagination.total_count} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package size={48} className="mx-auto mb-4 text-slate-300" />
              <p>Nenhum produto encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedIds.length === products.length && products.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-right">Preço</TableHead>
                    <TableHead className="text-center">Estoque</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow
                      key={product.id}
                      data-testid={`product-row-${product.id}`}
                      className={selectedIds.includes(product.id) ? 'bg-blue-50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(product.id)}
                          onCheckedChange={() => toggleSelect(product.id)}
                          data-testid={`select-product-${product.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell>
                        {product.category ? (<Badge variant="secondary">{product.category}</Badge>) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(product.price)}</TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center gap-1 ${isLowStock(product) ? 'text-amber-600' : 'text-slate-600'}`}>
                          {isLowStock(product) && <AlertTriangle size={14} />}
                          {product.stock} {product.unit}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(product)} className="text-slate-600 hover:text-blue-600">
                            <Pencil size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(product.id)} className="text-slate-600 hover:text-red-600">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t" data-testid="products-pagination">
              <p className="text-sm text-slate-500">
                Página {pagination.page} de {pagination.total_pages} ({pagination.total_count} produtos)
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>Primeira</Button>
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>Anterior</Button>
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.total_pages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= pagination.total_pages - 2) pageNum = pagination.total_pages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <Button key={pageNum} variant={currentPage === pageNum ? 'default' : 'outline'} size="sm"
                      className={currentPage === pageNum ? 'bg-blue-600' : ''} onClick={() => setCurrentPage(pageNum)}>
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={currentPage === pagination.total_pages} onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}>Próxima</Button>
                <Button variant="outline" size="sm" disabled={currentPage === pagination.total_pages} onClick={() => setCurrentPage(pagination.total_pages)}>Última</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar {selectedIds.length} Produto(s) em Massa</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Marque os campos que deseja alterar e preencha o novo valor. Apenas os campos marcados serão atualizados.
          </p>
          <div className="space-y-5 pt-2">
            {/* Price */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="bulk-price-check"
                checked={bulkFields.price}
                onCheckedChange={(v) => setBulkFields(prev => ({ ...prev, price: !!v }))}
                className="mt-2"
                data-testid="bulk-price-check"
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor="bulk-price" className={!bulkFields.price ? 'text-slate-400' : ''}>
                  Preço de Venda (R$)
                </Label>
                <Input
                  id="bulk-price"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={bulkData.price}
                  onChange={(e) => setBulkData(prev => ({ ...prev, price: e.target.value }))}
                  disabled={!bulkFields.price}
                  data-testid="bulk-price-input"
                />
              </div>
            </div>

            {/* Category */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="bulk-category-check"
                checked={bulkFields.category}
                onCheckedChange={(v) => setBulkFields(prev => ({ ...prev, category: !!v }))}
                className="mt-2"
                data-testid="bulk-category-check"
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor="bulk-category" className={!bulkFields.category ? 'text-slate-400' : ''}>
                  Categoria
                </Label>
                <Input
                  id="bulk-category"
                  placeholder="Ex: Calçados"
                  value={bulkData.category}
                  onChange={(e) => setBulkData(prev => ({ ...prev, category: e.target.value }))}
                  disabled={!bulkFields.category}
                  data-testid="bulk-category-input"
                />
              </div>
            </div>

            {/* Stock */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="bulk-stock-check"
                checked={bulkFields.stock}
                onCheckedChange={(v) => setBulkFields(prev => ({ ...prev, stock: !!v }))}
                className="mt-2"
                data-testid="bulk-stock-check"
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor="bulk-stock" className={!bulkFields.stock ? 'text-slate-400' : ''}>
                  Quantidade Atual (Estoque)
                </Label>
                <Input
                  id="bulk-stock"
                  type="number"
                  placeholder="0"
                  value={bulkData.stock}
                  onChange={(e) => setBulkData(prev => ({ ...prev, stock: e.target.value }))}
                  disabled={!bulkFields.stock}
                  data-testid="bulk-stock-input"
                />
              </div>
            </div>

            {/* CFOP */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="bulk-cfop-check"
                checked={bulkFields.cfop}
                onCheckedChange={(v) => setBulkFields(prev => ({ ...prev, cfop: !!v }))}
                className="mt-2"
                data-testid="bulk-cfop-check"
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor="bulk-cfop" className={!bulkFields.cfop ? 'text-slate-400' : ''}>
                  CFOP
                </Label>
                <Input
                  id="bulk-cfop"
                  placeholder="Ex: 5102"
                  value={bulkData.cfop}
                  onChange={(e) => setBulkData(prev => ({ ...prev, cfop: e.target.value }))}
                  disabled={!bulkFields.cfop}
                  data-testid="bulk-cfop-input"
                />
                <p className={`text-xs ${!bulkFields.cfop ? 'text-slate-300' : 'text-slate-500'}`}>
                  5102 = Venda mercadoria adquirida | 5405 = Venda ST
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleBulkSubmit}
              className="bg-amber-500 hover:bg-amber-600"
              disabled={bulkSaving || (!bulkFields.price && !bulkFields.category && !bulkFields.stock && !bulkFields.cfop)}
              data-testid="bulk-save-btn"
            >
              {bulkSaving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
              Aplicar Alterações
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Label Print Dialog */}
      <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="label-dialog">
          <DialogHeader>
            <DialogTitle>Imprimir Etiquetas de Precos</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Defina a quantidade de etiquetas para cada produto. Formato: 50x30mm com codigo de barras.
          </p>
          <div className="space-y-3 mt-2">
            {products.filter(p => selectedIds.includes(p.id)).map(product => (
              <div key={product.id} className="flex items-center gap-3 p-2 border rounded-lg bg-slate-50" data-testid={`label-item-${product.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{product.name}</p>
                  <p className="text-xs text-slate-500">
                    {formatCurrency(product.price)} | {product.ean && product.ean !== 'SEM GTIN' ? product.ean : (product.sku || 'S/ codigo')}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Label className="text-xs text-slate-500">Qtd:</Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={labelQuantities[product.id] || 1}
                    onChange={(e) => setLabelQuantities(prev => ({ ...prev, [product.id]: parseInt(e.target.value) || 1 }))}
                    className="w-16 h-8 text-center text-sm"
                    data-testid={`label-qty-${product.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-3 border-t mt-3">
            <span className="text-sm text-slate-500">
              Total: {Object.values(labelQuantities).reduce((sum, q) => sum + (parseInt(q) || 1), 0)} etiqueta(s)
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setLabelDialogOpen(false)}>Cancelar</Button>
              <Button onClick={printLabels} className="bg-blue-600 hover:bg-blue-700" data-testid="label-print-btn">
                <Printer size={16} className="mr-2" /> Imprimir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
